/*
  Shortlist ("save to shortlist" hearts) — ACCOUNT-GATED as of 2026-08-24.

  Item 39, both halves, finally shippable. The heart now requires a signed-in
  bride, and the list persists to her customer account.

  ⚠️ This REVERSES the previous decision, and the reason matters. A gate used to
  be a regression: storefront JS cannot write customer metafields, so gating
  without persistence meant a bride signed in AND still lost her shortlist on the
  next device. Adrian's n8n webhook + the `eddyk.shortlist` customer metafield
  definition removed that objection, so gate and persistence ship together, as
  agreed. Do not re-open the gate without also removing the persistence.

  Three states, and they are deliberately different:
    · signed out  — the heart does not save. It fires `ek:shortlist:signin-required`
                    and theme.liquid asks her to sign in. Nothing is written.
    · signed in   — localStorage stays the fast local mirror AND every change is
                    pushed to the account, debounced.
    · on load     — the ACCOUNT is the source of truth. The server list overwrites
                    the local mirror, so a shortlist saved on her phone appears on
                    her laptop rather than being silently shadowed by stale local
                    state.

  ⚠️ The webhook is full-replace and always answers 200 — `{ok:false,...}` in the
  body is how a failure arrives, never an HTTP status. Anything that only checks
  `response.ok` will believe every write succeeded.

  Stored value is just an ordered array of product handles, most recently saved
  first. Deliberately no titles or image URLs: the shortlist page re-reads each
  product from Shopify's own /products/{handle}.js, so a renamed gown or a
  changed photo is never stale, and a deleted one can be pruned.

  Anything with [data-ek-fav="{handle}"] becomes a toggle, and anything with
  [data-ek-fav-count] shows the running total. Binding is delegated from
  document, so cards added later still work — which matters on the gown grid,
  where pages 2-3 are appended after load.

  Public API: window.EKShortlist — get/count/has/add/remove/toggle/clear
  Event: window 'ek:shortlist:change' with { items, count }
*/
(function () {
  var KEY = 'ek:shortlist';
  var PENDING_KEY = KEY + ':pending';   // one gown, held across a sign-in round trip
  // ⚠️ Nothing writes PENDING_KEY any more — it belonged to the wall. The READ is
  // kept deliberately for a few weeks: a bride who tapped a heart before 14 Sep and
  // signs in afterwards still has one waiting, and finishing it costs nothing.
  var NOTE_KEY = KEY + ':noted';        // the device note, shown once per browser
  var CFG = window.EK_SHORTLIST_CONFIG || {};
  // ⚠️ This file is a plain asset, so Liquid's `| t` cannot be used here — the two
  // labels are handed in from layout/theme.liquid. `| t` HTML-escapes, and these go
  // to textContent, so they are decoded once. English fallbacks keep the toggle
  // working if the config is ever absent.
  var LBL = (function (o) {
    var d = document.createElement('textarea');
    function dec(t, fb) { if (!t) return fb; d.innerHTML = t; return d.value || fb; }
    return { save: dec(o.save, 'Save to shortlist'),
             saved: dec(o.saved, 'Saved to shortlist') };
  })(CFG.labels || {});
  var CUSTOMER_ID = CFG.customerId || null;      // numeric, or null when signed out
  var SIGNED_IN = CUSTOMER_ID !== null;

  // Private browsing can make localStorage throw on write; fall back to memory
  // so the UI still behaves for the length of the visit.
  var memory = null;
  var usable = (function () {
    try {
      localStorage.setItem(KEY + ':probe', '1');
      localStorage.removeItem(KEY + ':probe');
      return true;
    } catch (e) {
      return false;
    }
  })();

  function read() {
    if (!usable) return memory ? memory.slice() : [];
    try {
      var raw = localStorage.getItem(KEY);
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed.filter(function (h) { return typeof h === 'string' && h; });
    } catch (e) {
      return [];
    }
  }

  function write(list) {
    memory = list.slice();
    if (usable) {
      try { localStorage.setItem(KEY, JSON.stringify(list)); } catch (e) {}
    }
    emit();
  }

  function emit() {
    var items = read();
    window.dispatchEvent(new CustomEvent('ek:shortlist:change', {
      detail: { items: items, count: items.length }
    }));
  }

  var API = {
    get: read,
    count: function () { return read().length; },
    has: function (handle) { return read().indexOf(handle) !== -1; },
    add: function (handle) {
      if (!handle) return false;
      var list = read();
      if (list.indexOf(handle) === -1) {
        list.unshift(handle);          // newest first
        write(list);
      }
      return true;
    },
    remove: function (handle) {
      var list = read();
      var i = list.indexOf(handle);
      if (i !== -1) {
        list.splice(i, 1);
        write(list);
      }
      return false;
    },
    toggle: function (handle) {
      return API.has(handle) ? API.remove(handle) : API.add(handle);
    },
    clear: function () { write([]); },
    available: usable
  };

  // ------------------------------------------------ persistence to the account

  // Debounced: hearting three gowns in a row is one write, not three. Full
  // replace, so the last call always describes the whole list correctly even if
  // an earlier one was dropped.
  var pushTimer = null;
  function persist() {
    if (!SIGNED_IN || !CFG.endpoint) return;
    clearTimeout(pushTimer);
    pushTimer = setTimeout(function () {
      var handles = read();
      fetch(CFG.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customer_id: CUSTOMER_ID, handles: handles }),
        keepalive: true          // survive the bride navigating away mid-save
      }).then(function (r) { return r.json(); }).then(function (body) {
        // ⚠️ Always 200. The verdict is in the body.
        if (!body || body.ok !== true) {
          console.warn('[shortlist] not saved to the account:', body);
          window.dispatchEvent(new CustomEvent('ek:shortlist:save-failed',
            { detail: { body: body } }));
        }
      }).catch(function (err) {
        console.warn('[shortlist] save request failed:', err);
        window.dispatchEvent(new CustomEvent('ek:shortlist:save-failed',
          { detail: { error: String(err) } }));
      });
    }, 600);
  }

  window.EKShortlist = API;

  // ⚠️ EVERY WRITE HERE IS GUARDED BY A READ-BACK, and that is the point rather
  // than tidiness. Setting el.style.display invalidates layout even when the value
  // is identical, and this function used to run on every frame during page parse
  // (see the observer at the bottom). Assigning only on a real change is what
  // turns it from a layout-thrasher into a no-op on the passes where nothing
  // changed — which is almost all of them.
  function syncToggles() {
    var saved = read();
    Array.prototype.forEach.call(document.querySelectorAll('[data-ek-fav]'), function (el) {
      var on = saved.indexOf(el.getAttribute('data-ek-fav')) !== -1;
      if (el.classList.contains('on') !== on) el.classList.toggle('on', on);
      var pressed = on ? 'true' : 'false';
      if (el.getAttribute('aria-pressed') !== pressed) el.setAttribute('aria-pressed', pressed);
      var label = el.querySelector('[data-ek-fav-label]');
      if (label) {
        var text = on ? LBL.saved : LBL.save;
        if (label.textContent !== text) label.textContent = text;
      }
    });
    var countText = String(saved.length);
    var display = saved.length ? 'flex' : 'none';
    Array.prototype.forEach.call(document.querySelectorAll('[data-ek-fav-count]'), function (el) {
      if (el.textContent !== countText) el.textContent = countText;
      if (el.style.display !== display) el.style.display = display;
    });
  }

  // ---- analytics -------------------------------------------------------
  // ⛔ gtag DIRECTLY, NOT Shopify.analytics.publish, AND THAT IS DELIBERATE.
  // The theme's other five EK events (ek_lead, ek_sheet_open, ek_find_boutique,
  // ek_rsvp_open) go through Shopify.analytics.publish, which only arrives
  // somewhere if one of this store's five CUSTOM web pixels forwards it. Those
  // pixels run inside a sandboxed iframe, so from the page it is impossible to
  // tell whether any of them do: a probe published on 11 Sep 2026 produced no
  // visible GA request, which proves nothing either way because the iframe's
  // own traffic cannot be seen from here.
  //
  // What Adrian asked for is a number in Google Analytics. gtag is already
  // loaded on every page (G-B54QN1FG8P) and its page_view hits are provably
  // landing, so this is the one route that is certain rather than assumed.
  //
  // ⚠️ DO NOT ALSO PUBLISH THESE THROUGH Shopify.analytics. If a pixel does
  // forward ek_* to the same GA4 property, every heart would be counted twice —
  // and it would inflate exactly the number the wall decision rests on, in the
  // direction that flatters the argument for removing it.
  //
  // ⚠️ Wrapped, like every other analytics call in this theme: a bride must
  // never lose her shortlist because a measurement threw.
  function track(name, params) {
    try {
      if (typeof window.gtag === 'function') window.gtag('event', name, params || {});
    } catch (e) {}
  }

  document.addEventListener('click', function (e) {
    var el = e.target.closest ? e.target.closest('[data-ek-fav]') : null;
    if (!el) return;
    // Hearts sit inside the card's <a>, so the navigation has to be stopped.
    // This happens BEFORE the gate check, or a blocked heart would follow the
    // card link and look like the site had simply navigated away.
    e.preventDefault();
    e.stopPropagation();

    // ⭐ NO GATE. The wall came off on 14 Sep 2026: every tap saves, signed in or
    // not. The decision rests on three days of ek_heart_blocked — 58 brides turned
    // away against about 16 accounts a week — so the event that justified it is
    // also the event that has now been deleted, because "blocked" can no longer
    // happen.

    // Read BEFORE toggling, so we know which way it went. API.toggle removes a
    // gown that is already saved, and an un-save is not a save.
    var qzH = el.getAttribute('data-ek-fav');
    var wasSaved = API.has(qzH);
    API.toggle(qzH);
    persist();                   // ⚠️ a no-op when signed out: the list is local only
    // signed_in rides along so Wednesday's read can separate the two populations
    // without needing a second event name.
    track(wasSaved ? 'ek_heart_unsave' : 'ek_heart_save',
          { gown: qzH || '', signed_in: SIGNED_IN ? 'yes' : 'no' });

    // ⭐ THE NOTE, ONCE PER BROWSER, ON HER FIRST SAVE WHILE SIGNED OUT. The old
    // card fired on EVERY blocked tap, and it had to: it was explaining why
    // nothing had happened. This one is an invitation, and an invitation repeated
    // on every heart is nagging — so it is shown once and then never again.
    if (!SIGNED_IN && !wasSaved) {
      var noted = null;
      try { noted = localStorage.getItem(NOTE_KEY); } catch (e) {}
      if (!noted) {
        try { localStorage.setItem(NOTE_KEY, '1'); } catch (e) {}
        window.dispatchEvent(new CustomEvent('ek:shortlist:device-note',
          { detail: { handle: qzH } }));
      }
    }
  });

  // ---- the HEADER heart, 2026-08-28 (Kike) -------------------------------
  // It is an <a href="/pages/shortlist"> injected by theme.liquid, NOT a
  // [data-ek-fav] toggle, so the gate above never saw it: signed out it
  // navigated to a shortlist page that can only ever be empty, with no
  // explanation. It now raises the SAME prompt the gown hearts raise.
  //
  // Delegated on document rather than bound to the element, deliberately.
  // theme.liquid warns that Horizon's <header-actions> is a reactive custom
  // element that re-renders its own children and 'can silently wipe out a
  // one-time DOM patch' - which is exactly what an element-bound listener
  // would be. Delegation cannot be wiped.
  //
  // No stopPropagation(): unlike the gown hearts, this element IS the link,
  // so there is no wrapping <a> to suppress and nothing to gain from
  // silencing other document-level listeners.
  // ⭐ 14 Sep 2026: nothing to intercept any more. With the wall gone the header
  // heart is just a link to her shortlist, for everyone, which is what it looked
  // like all along. The delegated listener that used to block it is deleted
  // rather than left switched off, so nobody has to wonder later whether it
  // still fires.

  window.addEventListener('ek:shortlist:change', syncToggles);
  window.addEventListener('storage', function (e) {
    if (e.key === KEY) emit();          // keep other tabs in step
  });

  // ⚠️ On load the ACCOUNT wins, not localStorage. Without this a bride who
  // hearted three gowns on her phone would open her laptop, see the laptop's
  // stale local list, and the next save would push that stale list back over the
  // good one — a full-replace endpoint turns a read bug into data loss.
  // A signed-OUT visitor's local list is left alone but never acted on; it is
  // simply dormant until she signs in.
  if (SIGNED_IN && Array.isArray(CFG.server)) {
    var local = read();
    var server = CFG.server.filter(function (h) { return typeof h === 'string' && h; });
    // ⛔ UNION, NOT REPLACE — and this is the part of taking the wall off that
    // could actually lose a bride's gowns. While the wall was up a signed-out
    // visitor could save nothing, so letting the account overwrite the local
    // mirror was safe. From today she can sign in carrying a list built on her
    // phone; replacing it with an empty account list would delete exactly the
    // gowns the sign-in note promised to protect.
    //
    // Server order first — it is the canonical list and the one other devices
    // will see — then anything local the account has not got yet.
    var merged = server.slice();
    for (var mi = 0; mi < local.length; mi++) {
      if (merged.indexOf(local[mi]) === -1) merged.push(local[mi]);
    }
    if (merged.join(' ') !== local.join(' ')) {
      write(merged);
    }
    // ⚠️ And push it up, or what her phone brought would live on this device only
    // until the next save — which is the promise broken in the other direction.
    if (merged.join(' ') !== server.join(' ')) {
      persist();
    }
  }

  // ⭐ AND NOW FINISH WHAT SHE STARTED. She hearted a gown, was asked to sign in,
  // signed in, and came back. Save it for her rather than making her ask twice.
  //
  // ⚠️ DELIBERATELY AFTER THE BLOCK ABOVE, and the order is the whole point: that
  // block lets the ACCOUNT overwrite the local mirror. Applying the pending gown
  // first would see it wiped one line later, and she would read that as the sign-in
  // having failed.
  //
  // ⚠️ persist() matters too -- without it the gown would live on this device only,
  // which is precisely the thing signing in was supposed to fix.
  if (SIGNED_IN) {
    var pending = null;
    try { pending = localStorage.getItem(PENDING_KEY); } catch (e) {}
    if (pending) {
      try { localStorage.removeItem(PENDING_KEY); } catch (e) {}
      if (!API.has(pending)) {
        API.add(pending);
        persist();
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncToggles);
  } else {
    syncToggles();
  }

  // The gown grid appends pages 2-3 after load; re-sync when the DOM grows so
  // those hearts show the right state without each section knowing about this.
  //
  // ⛔ THIS WAS THE SECOND-MOST EXPENSIVE THING ON A GOWN PAGE: 1,215 ms of
  // bootup time, measured 3 Sep, beaten only by the document itself. Three
  // separate faults, all in these few lines:
  //   · it observed document.documentElement with subtree:true and started
  //     BEFORE the parser had finished, so it fired on essentially every batch
  //     of nodes the page was still parsing;
  //   · each fire ran syncToggles(), which is two full-document
  //     querySelectorAll passes;
  //   · syncToggles wrote el.style.display unconditionally and the next pass
  //     read the DOM again — a write/read pair per frame, which is the classic
  //     forced-reflow loop. Style & Layout was 4.8 s of an 8.3 s main thread.
  //
  // ⭐ THREE THINGS CHANGED, AND THE BEHAVIOUR IS IDENTICAL. It starts only once
  // the document is parsed — nothing is missed, because DOMContentLoaded already
  // runs a sync. It ignores mutations that do not actually carry a heart or a
  // count. And syncToggles above no longer writes unless a value really differs.
  //
  // ⚠️ NOT DISCONNECTED AFTER LOAD, deliberately: the gown grid appends pages
  // 2-3 long after load, and disconnecting would leave those hearts blank. The
  // fix is to make it cheap, not to switch it off.
  function watchForLateHearts() {
    if (!window.MutationObserver) return;
    var pending = false;
    function carriesHeart(node) {
      if (!node || node.nodeType !== 1) return false;
      if (node.hasAttribute('data-ek-fav') || node.hasAttribute('data-ek-fav-count')) return true;
      return !!node.querySelector('[data-ek-fav], [data-ek-fav-count]');
    }
    new MutationObserver(function (records) {
      if (pending) return;
      for (var i = 0; i < records.length; i++) {
        var added = records[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          if (carriesHeart(added[j])) {
            pending = true;
            requestAnimationFrame(function () { pending = false; syncToggles(); });
            return;
          }
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchForLateHearts);
  } else {
    watchForLateHearts();
  }
})();
